const WORK_ITEM_SELECTOR = '.works-sec__item.image-type';
const MEDIA_CONTAINER_SELECTOR = '.works-sec__item-media';

const LAZY_ATTRS = {
    src: 'data-work-lazy-src',
    srcset: 'data-work-lazy-srcset',
    poster: 'data-work-lazy-poster',
};

const preparedMedia = new WeakSet();
const loadedMedia = new WeakSet();
const loadingMedia = new WeakSet();

function moveAttrToLazy(el, attrName) {
    const current = el.getAttribute(attrName);

    if (!current) {
        return;
    }

    const lazyAttr = LAZY_ATTRS[attrName];

    if (!el.hasAttribute(lazyAttr)) {
        el.setAttribute(lazyAttr, current);
    }

    el.removeAttribute(attrName);
}

function prepareMedia(item) {
    if (preparedMedia.has(item)) {
        return;
    }

    preparedMedia.add(item);

    const media = item.querySelector(MEDIA_CONTAINER_SELECTOR);

    if (!media) {
        return;
    }

    const lazyTargets = media.querySelectorAll('img, source, video');

    if (!lazyTargets.length) {
        return;
    }

    lazyTargets.forEach((target) => {
        moveAttrToLazy(target, 'src');
        moveAttrToLazy(target, 'srcset');
        moveAttrToLazy(target, 'poster');
    });
}

function markItemLoaded(item) {
    item.classList.add('is-loaded');
    loadedMedia.add(item);
    loadingMedia.delete(item);
}

function waitForImageLoad(img) {
    return new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
        }

        const done = () => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            resolve();
        };

        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
    });
}

function waitForVideoReady(video) {
    return new Promise((resolve) => {
        if (video.readyState >= 2) {
            resolve();
            return;
        }

        const done = () => {
            video.removeEventListener('loadeddata', done);
            video.removeEventListener('error', done);
            resolve();
        };

        video.addEventListener('loadeddata', done, { once: true });
        video.addEventListener('error', done, { once: true });
    });
}

async function loadItemMedia(item) {
    if (loadedMedia.has(item) || loadingMedia.has(item)) {
        return;
    }

    const media = item.querySelector(MEDIA_CONTAINER_SELECTOR);

    if (!media) {
        return;
    }

    const lazyTargets = media.querySelectorAll('img, source, video');

    if (!lazyTargets.length) {
        return;
    }

    loadingMedia.add(item);

    lazyTargets.forEach((target) => {
        const lazySrc = target.getAttribute(LAZY_ATTRS.src);
        const lazySrcset = target.getAttribute(LAZY_ATTRS.srcset);
        const lazyPoster = target.getAttribute(LAZY_ATTRS.poster);

        if (lazySrc) {
            target.setAttribute('src', lazySrc);
        }

        if (lazySrcset) {
            target.setAttribute('srcset', lazySrcset);
        }

        if (lazyPoster) {
            target.setAttribute('poster', lazyPoster);
        }
    });

    const img = media.querySelector('img');
    const video = media.querySelector('video');

    if (img) {
        await waitForImageLoad(img);
        markItemLoaded(item);
        return;
    }

    if (video) {
        if (typeof video.load === 'function') {
            video.load();
        }

        await waitForVideoReady(video);

        const playPromise = typeof video.play === 'function' ? video.play() : null;

        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
        }

        markItemLoaded(item);
        return;
    }

    markItemLoaded(item);
}

function initWorksMediaLazyLoad() {
    const workItems = document.querySelectorAll(WORK_ITEM_SELECTOR);

    if (!workItems.length) {
        return;
    }

    workItems.forEach((item) => {
        prepareMedia(item);

        item.addEventListener('mouseenter', () => {
            loadItemMedia(item);
        });

        item.addEventListener('focusin', () => {
            loadItemMedia(item);
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWorksMediaLazyLoad, { once: true });
} else {
    initWorksMediaLazyLoad();
}
